import { useEffect, useState } from 'react';

import type { AudioPlaybackPort } from '@/shared-responder-v2-core';

import { createBrowserAudioPlaybackPort } from './shared-responder-v2-media-audio-playback';

export interface ResponderAudioSession {
  readonly playback: AudioPlaybackPort;
  /**
   * The element the port actually plays through. A screen recording mixes it in
   * so the saved video carries the assistant's side of the conversation, which
   * is only possible if the two share one element rather than each minting its
   * own.
   */
  readonly element: HTMLAudioElement | null;
}

function bumpRenderToken(token: number): number {
  return token + 1;
}

function createAudioElement(): HTMLAudioElement | null {
  return typeof Audio === 'undefined' ? null : new Audio();
}

// test-exception: React shell only — every playback branch lives in createBrowserAudioPlaybackPort, covered by shared-responder-v2-media-audio-playback.spec.ts.
export function useResponderAudioSession(): ResponderAudioSession {
  const [, setRenderToken] = useState(0);

  const [session] = useState(() => {
    const element = createAudioElement();

    return {
      element,
      playback: createBrowserAudioPlaybackPort(
        () => setRenderToken(bumpRenderToken),
        element
      ),
    };
  });

  useEffect(() => () => session.playback.stop(), [session]);

  return session;
}
